import { clsx } from "clsx";
import type { CSSProperties, FC, PropsWithChildren, ReactElement } from "react";

import { useDataAttributes } from "../../hooks.ts";
import styleSkeleton from "../../styles/loading_skeleton.module.css";
import { Label } from "../Label/Label.tsx";
import { VStack } from "../Stack/VStack.tsx";
import style from "./CardContent.module.css";

export interface InfoCardRowProps {
  label?: string | ReactElement;
  /**
   * Use it only if you want is to be represented as an array.
   * Use Children instead for other use cases.
   */
  value?: string | number | ReactElement | ReactElement[] | string[] | null | undefined;
  /**
   * Limit the width of the row children (useful with flex elements, like the tiles)
   */
  maxWidth?: number;
}

const CardRowSimple: FC<PropsWithChildren<InfoCardRowProps>> = (props) => {
  let content = props.value;
  const dataAttributes = useDataAttributes(props);

  // the array is usually an array of numbers
  if (Array.isArray(content)) {
    content = (
      <div className={style.grid} {...dataAttributes}>
        {content.map((value, index) => (
          <div key={`value-col-${index}`}>{value}</div>
        ))}
      </div>
    );
  }

  return props.value || props.children ? (
    // Need a container per section
    <VStack spacing={2}>
      {props.label != undefined && <Label>{props.label}</Label>}
      {/*{props.title && <div className={style.title}>{props.title}</div>}*/}
      <div className={style.info} style={{ maxWidth: props.maxWidth }}>
        {props.value ? content : props.children}
      </div>
    </VStack>
  ) : null;
};

const CardRowSkeleton: FC<PropsWithChildren<InfoCardRowProps>> = (props) => {
  return (
    <VStack spacing={2}>
      <Label>
        <span className={styleSkeleton.skeleton}>Lorem ipsum dolor</span>
      </Label>
      <div className={clsx(style.info)} style={{ maxWidth: props.maxWidth, display: "inline-block" }}>
        <span className={styleSkeleton.skeleton}>Lorem ipsum dolor sit amet.</span>
      </div>
    </VStack>
  );
};

const CardRowDetail: FC<
  PropsWithChildren<Omit<InfoCardRowProps, "label"> & { title?: string | ReactElement } & { style?: CSSProperties }>
> = ({ children, ...otherProps }) => (
  <CardRowSimple {...otherProps} label={otherProps.title}>
    <div className={style.card_row_simple} style={{ maxWidth: otherProps.maxWidth, ...otherProps.style }}>
      {children}
    </div>
  </CardRowSimple>
);

const CardRowDetailColumn: FC<PropsWithChildren> = ({ children }) => (
  <table>
    <tbody>{children}</tbody>
  </table>
);

const CardRowDetailLine: FC<PropsWithChildren<{ title: string | ReactElement; wrap?: boolean }>> = ({
  title,
  wrap,
  children,
}) => (
  <tr>
    <th className={style.card_row_detail_th}>{title}</th>
    <td style={{ whiteSpace: wrap !== true ? "nowrap" : undefined }}>{children}</td>
  </tr>
);

const CardRowDetailLineSpacer: FC = () => (
  <tr>
    <td colSpan={2} className={style.car_row_spacer} />
  </tr>
);

export {
  CardRowDetail,
  CardRowDetailColumn,
  CardRowDetailLine,
  CardRowDetailLineSpacer,
  CardRowSimple,
  CardRowSkeleton,
};
